"use client";

import { Box, Button, Center, Icon, Text } from "@chakra-ui/react";
import { PdfViewer } from "@/_components/ui/PdfViewer/PdfViewer";
import { UnlockPrompt } from "@/_components/ui/UnlockPrompt";
import { Emoji } from "@/_components/ui/Emoji/Emoji";
import { FullViewResume } from "@/_models/resume";
import { useTranslations } from "next-intl";
import { FaLock } from "react-icons/fa";
import { FaDownload } from "react-icons/fa6";
import {
  LOCKED_RESUME_PREVIEW_PDF_URL,
  LOCKED_PREVIEW_DOWNLOAD_BASENAME,
  RESUME_DETAIL_RESUME_NS,
  downloadPdf,
} from "./constants";

const downloadLockedButtonFn = async () => {
  await downloadPdf(
    LOCKED_RESUME_PREVIEW_PDF_URL,
    LOCKED_PREVIEW_DOWNLOAD_BASENAME,
  );
};

export interface OriginalVersionContentProps {
  fullResume?: FullViewResume;
  onClickUnlock: () => void;
  isLoading?: boolean;
}

export function ResumeLock({
  fullResume,
  onClickUnlock,
  isLoading,
}: OriginalVersionContentProps) {
  const t = useTranslations(RESUME_DETAIL_RESUME_NS);

  return (
    <Center
      id="resume-original-version-content"
      boxSize="full"
      position="relative"
      rounded="lg"
      overflow="auto"
    >
      {fullResume?.download_url ? (
        <Box
          w="100%"
          h="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Box h="100%" w="full">
            <PdfViewer fileUrl={fullResume.download_url} />
          </Box>
        </Box>
      ) : (
        <Box position="relative" w="100%" h="100%" minH="200px">
          {LOCKED_RESUME_PREVIEW_PDF_URL ? (
            <>
              <Box position="absolute" top={2} right={3} zIndex={11}>
                <Button
                  visual="solid"
                  colorPalette="primary"
                  size="sm"
                  onClick={downloadLockedButtonFn}
                  _hover={{ px: "4" }}
                  transition="all 0.2s ease-in-out"
                  roundedRight="none"
                  roundedTopLeft="none"
                  roundedBottomLeft="2xl"
                  roundedTopRight="md"
                >
                  <Icon as={FaDownload} />
                  <Text as="span" fontSize="xs" color="inherit">
                    {t("field.knoot_outline")}
                  </Text>
                </Button>
              </Box>
              <Box
                position="absolute"
                inset={0}
                overflow="hidden"
                rounded="lg"
                aria-hidden
              >
                <PdfViewer
                  fileUrl={LOCKED_RESUME_PREVIEW_PDF_URL}
                  height="100%"
                  showToolbar={false}
                />
              </Box>
            </>
          ) : null}
          <UnlockPrompt
            icon={<Emoji emoji="high-voltage" size={6} />}
            title={t("unlock.original_title")}
            description={
              <>
                <Text
                  as="span"
                  fontWeight="bold"
                  color="orange.500"
                  textTransform="uppercase"
                >
                  {t("unlock.unlock_now")}
                </Text>{" "}
                {t("unlock.original_description")}
              </>
            }
            buttonText={t("unlock.unlock_now_button")}
            buttonIcon={FaLock}
            onUnlock={onClickUnlock}
            isLoading={isLoading}
            isAbsolute={true}
          />
        </Box>
      )}
    </Center>
  );
}
